import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  type ListenerOptions,
  type MessageHandler,
  type SendMessageOptions,
  type WebviewBridgeConfig,
  type WebviewMessage,
  type WebviewResponse,
} from './webview-bridge.type';
import { WebviewBridgeService } from './webview-bridge-service';

interface WebviewBridgeContextValue {
  /**
   * Send a message to the native webview
   */
  send: <T = Record<string, unknown>, R = Record<string, unknown>>(
    type: string,
    data: T,
    options?: SendMessageOptions
  ) => Promise<WebviewResponse<R> | undefined>;

  /**
   * Register a handler for a specific message type
   */
  on: <T = Record<string, unknown>>(type: string, handler: MessageHandler<T>, options?: ListenerOptions) => () => void;

  /**
   * Register a one-time handler for a specific message type
   */
  once: <T = Record<string, unknown>>(type: string, handler?: MessageHandler<T>) => Promise<WebviewMessage<T>>;

  /**
   * Remove all handlers for a specific message type
   */
  off: (type: string) => void;

  /**
   * Remove all handlers
   */
  removeAllListeners: () => void;

  /**
   * Check if bridge is initialized
   */
  isInitialized: boolean;
}

const WebviewBridgeContext = createContext<WebviewBridgeContextValue | undefined>(undefined);

interface WebviewBridgeProviderProps {
  children: React.ReactNode;
  config?: WebviewBridgeConfig;
}

/**
 * Webview Bridge Provider
 *
 * Provides webview bridge functionality to all child components.
 * Automatically initializes and cleans up the bridge service.
 */
export const WebviewBridgeProvider = ({ children, config }: WebviewBridgeProviderProps) => {
  const serviceRef = useRef<WebviewBridgeService | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const initStartedRef = useRef(false);
  // Freeze config on first mount. Bridge config is semantically static
  // (allowedOrigin, messagePrefix); rebuilding the service on every config
  // object-identity change would drop listeners and reject pending requests.
  const configRef = useRef(config);

  useEffect(() => {
    if (!serviceRef.current) {
      serviceRef.current = new WebviewBridgeService(configRef.current);
    }

    const service = serviceRef.current;

    if (!initStartedRef.current) {
      service.initialize();
      initStartedRef.current = true;
      setIsInitialized(true);
    }

    return () => {
      serviceRef.current?.destroy();
      serviceRef.current = null;
      initStartedRef.current = false;
      setIsInitialized(false);
    };
  }, []);

  const contextValue = useMemo<WebviewBridgeContextValue>(() => {
    const service = serviceRef.current;
    if (!service || !isInitialized) {
      const notInitializedError = () => new Error('Webview bridge service not initialized');
      return {
        send: () => Promise.reject(notInitializedError()),
        on: () => () => {
          // no-op
        },
        once: () => Promise.reject(notInitializedError()),
        off: () => {
          // no-op
        },
        removeAllListeners: () => {
          // no-op
        },
        isInitialized: false,
      };
    }

    return {
      send: <T = Record<string, unknown>, R = Record<string, unknown>>(
        type: string,
        data: T,
        options?: SendMessageOptions
      ) => service.send<T, R>(type, data, options),
      on: <T = Record<string, unknown>,>(type: string, handler: MessageHandler<T>, options?: ListenerOptions) =>
        service.on<T>(type, handler, options),
      once: <T = Record<string, unknown>,>(type: string, handler?: MessageHandler<T>) => service.once<T>(type, handler),
      off: (type: string) => service.off(type),
      removeAllListeners: () => service.removeAllListeners(),
      isInitialized: true,
    };
  }, [isInitialized]);

  return <WebviewBridgeContext.Provider value={contextValue}>{children}</WebviewBridgeContext.Provider>;
};

/**
 * Hook to access webview bridge functionality
 *
 * @throws Error if used outside WebviewBridgeProvider
 */
export const useWebviewBridge = (): WebviewBridgeContextValue => {
  const context = useContext(WebviewBridgeContext);
  if (context === undefined) {
    throw new Error('useWebviewBridge must be used within a WebviewBridgeProvider');
  }
  return context;
};
